import {
    AfterViewInit,
    ChangeDetectionStrategy,
    Component,
    ElementRef,
    EventEmitter,
    Input,
    NgZone,
    OnChanges,
    OnDestroy,
    OnInit,
    Output,
    ViewChild
} from '@angular/core';
import { ControlValueAccessor, NG_VALUE_ACCESSOR, NgModel } from '@angular/forms';
import { ActivatedRoute } from '@angular/router';
import { ViewMode } from '@common/models/view-mode';
import { marker } from '@common/translate-marker';
import { IconName } from '@fortawesome/fontawesome-svg-core';
import { TranslateService } from '@ngx-translate/core';
import { DateInputCustomFormatPlaceholder, PopupSettings } from '@progress/kendo-angular-dateinputs';
import { filter, isArray, isDate, isString, map } from 'lodash-es';
import { Subject } from 'rxjs';
import { debounceTime } from 'rxjs/operators';
import { environment } from '../../../../environments/environment';
import { Size } from '../checkbox/checkbox.component';

export enum AppControlType {
    String = 'string',
    Textarea = 'textarea',
    Boolean = 'boolean',
    DateTime = 'datetime',
    Number = 'number',
    Array = 'array',
    Password = 'password',
    CodeList = 'codelist',
    File = 'file',
    YesNo = 'yesno',
    Select = 'select',
    Static = 'static'
}

const noop = (item) => `${item.code} - ${item.name}`;

// TODO:  - split codelist component
//        - remote filtering

@Component({
    selector: 'app-control',
    templateUrl: 'app-control.component.html',
    styleUrls: ['app-control.component.scss'],
    providers: [
        {
            provide: NG_VALUE_ACCESSOR,
            useExisting: AppControlComponent,
            multi: true
        }
    ],
    changeDetection: ChangeDetectionStrategy.Eager,
    standalone: false
})
export class AppControlComponent implements OnInit, AfterViewInit, OnDestroy, OnChanges, ControlValueAccessor {
    fieldTextType: boolean;

    @ViewChild('textareaEl') private textareaEl?: ElementRef<HTMLTextAreaElement>;
    @ViewChild('textareaActions', { read: ElementRef }) private textareaActions?: ElementRef<HTMLElement>;

    @ViewChild('tooltip', { static: false }) set tooltip(value) {
        this._tooltip = value;
        if (value) {
            this.showValidationErrors();
        }
    }

    get tooltip() {
        return this._tooltip;
    }

    get navigationProperty() {
        if (!this.property) {
            return this.property;
        }
        return this.property;
    }

    set value(value) {
        // this value is updated by programmatic changes if( val !== undefined && this.val !== val){
        if (this.type === AppControlType.Number) {
            this.val = this.roundNumber(value);
        } else {
            this.val = value;
        }
        this.onChange(value);
        this.onTouch(value);
        this.scheduleTextareaLayout();
    }

    get value() {
        return this.val;
    }
    get dateValue() {
        if (isDate(this.val)) {
            return this.val;
        }

        if (isString(this.val)) {
            const date = new Date(this.val);

            if (!date || !this._date || date.getTime() !== this._date.getTime()) {
                this._date = date;
            }

            return this._date;
        }

        return this.val;
    }

    set dateValue(value) {
        this._date = value;

        if (isDate(value)) {
            this.value = value.toISOString();
        } else {
            this.value = value;
        }
    }

    constructor(
        private activatedRoute: ActivatedRoute,
        private translateService: TranslateService,
        private ngZone: NgZone
    ) {}
    AppControlType: typeof AppControlType = AppControlType;
    errors: any[] = [];

    filteredOptions: any[] = [];

    showTooltip = environment.settings.validation.errors.showTooltip;
    showFeedback = environment.settings.validation.errors.showFeedback;

    isBusy = false;

    private val;
    private _subscription;
    private _syntheticProperty;
    private _tooltip;

    minYear = 1;

    public dateTimeFormatPlaceholder: DateInputCustomFormatPlaceholder;
    public dateFormatPlaceholder: Pick<DateInputCustomFormatPlaceholder, 'year' | 'month' | 'day'>;
    public timeFormatPlaceholder: Pick<DateInputCustomFormatPlaceholder, 'hour' | 'minute'>;

    @Input() options: any[] = [];
    @Input() type: AppControlType;
    @Input() label: string;
    @Input() labelTooltip: string;
    @Input() icon: IconName;
    @Input() ngModel: NgModel;
    @Input() modelPath?: string;
    @Input() path?: string;
    @Input() property?: string;
    @Input() codelist?: string;
    @Input() selectLabel = noop;
    @Input() fetch: (search, selectedValue) => Promise<any[]>;
    @Input() size: Size = Size.normal;
    @Input() multi = false;
    @Input() kendoType = 'datetime';
    @Input() isInTable = false;
    @Input() decimal: boolean;
    @Input() year: boolean;
    @Input() forFilter: boolean;
    @Input() min;
    @Input() max;
    @Input() static = false;
    @Input() isDisabled;
    @Input() isInvalid;
    @Input() formControlValidators;
    @Input() contextual: string = null;
    @Input() hasValue = false;
    @Input() margin = true;
    @Input() placeholder: string;
    /** Textbox and textarea only. Stops the input at this many characters. */
    @Input() maxLength: number;
    @Input() nullableCheckbox: boolean = false;
    @Input() buttonIcon: string;
    @Input() buttonTooltip: string;
    @Input() hidePickerButton = false;
    @Input() datePopupSettings: PopupSettings;

    /**
     * Textarea only. Shows the field as a single clipped line while it isn't being edited, with
     * buttons to expand it and copy its full value once the text doesn't fit on that line.
     */
    @Input() collapsible = false;

    @Input() passwordAutocomplete: string;
    @Input() roundNumberToPlaces = 0;

    @Output() textboxWithButtonClick = new EventEmitter<void>();

    private childEntityName: string;
    private fkName: string;

    /*
        if Input placeholder is passed to component, store it here and reuse in multiSelectFocusInHandler handler    
        if Input placeholder is not passed to component, placeholder is created from label property (see multiSelectFocusInHandler)
    */
    private originalPlaceholder: string;

    currentDateValue$ = new Subject<Date>();

    /** Collapsible textarea: currently showing its full height instead of one clipped line. */
    textareaExpanded = false;
    /** Collapsible textarea: the text needs more than the one collapsed line. */
    textareaNeedsCollapse = false;

    // Set when the user expanded the field with the button, so it stays open after focus leaves.
    private textareaPinnedOpen = false;
    private textareaResizeObserver?: ResizeObserver;
    // Typed `any`: this project's global `setTimeout`/`clearTimeout` resolve ambiguously between
    // the DOM (`number`) and @types/node (`NodeJS.Timeout`) overloads depending on build context.
    private textareaLayoutTimeout?: any;

    _date: Date;

    roundModifier = 0;

    maybeApplyActive() {
        this.hasValue =
            !!this.value ||
            (this.value === 0 && this.type === AppControlType.Number) ||
            (this.value === false && this.type === AppControlType.YesNo);
    }

    multiSelectFocusInHandler() {
        this.hasValue = !!this.value || !this.isDisabled;

        if (this.originalPlaceholder) {
            this.placeholder = this.originalPlaceholder;
        } else if (this.label) {
            this.placeholder = this.translateService.instant(marker('Input')) + ' ' + this.label;
        }
    }

    multiSelectFocusOutHandler() {
        this.hasValue = false;
        this.maybeApplyActive();
        this.placeholder = null;
    }

    onChange: any = () => {};
    onTouch: any = () => {};

    ngOnInit() {
        this.roundModifier = this.roundNumberToPlaces > 0 ? Math.pow(10, this.roundNumberToPlaces) : 0;
        this.originalPlaceholder = this.placeholder;

        if (!this.type) {
            this.type = AppControlType.String;
        }

        if (this.type === AppControlType.DateTime) {
            this.dateTimeFormatPlaceholder = this.getDateTimeFormatPlaceHolder();
            this.dateFormatPlaceholder = this.getDateFormatPlaceHolder();
            this.timeFormatPlaceholder = this.getTimeFormatPlaceholder();
        }

        this._syntheticProperty = this.navigationProperty;

        if (
            this.isDisabled === undefined &&
            this.activatedRoute.snapshot.data &&
            this.activatedRoute.snapshot.data.mode === ViewMode.view
        ) {
            this.isDisabled = true;
        }

        this.currentDateValue$.pipe(debounceTime(1000)).subscribe((currDate: Date) => {
            if (currDate?.getFullYear() <= this.minYear) {
                currDate.setFullYear(this.minYear);
                this.dateValue = currDate;
            }
        });
    }

    dateTimePickerValueChange = (date: Date) => this.currentDateValue$.next(date);

    ngAfterViewInit() {
        if (!this.isCollapsibleTextarea || !this.textareaEl || typeof ResizeObserver === 'undefined') {
            return;
        }

        // The wrapping (and so whether the text fits on one line) changes with the field's width.
        // The observer callback runs outside Angular's zone, so re-enter it to refresh the view.
        this.textareaResizeObserver = new ResizeObserver(() => this.ngZone.run(() => this.checkTextareaOverflow()));
        this.textareaResizeObserver.observe(this.textareaEl.nativeElement);
        this.scheduleTextareaLayout();
    }

    ngOnDestroy() {
        if (this.currentDateValue$) {
            this.currentDateValue$.unsubscribe();
        }

        this.textareaResizeObserver?.disconnect();
        clearTimeout(this.textareaLayoutTimeout);
    }

    private get isCollapsibleTextarea(): boolean {
        return this.collapsible && this.type === AppControlType.Textarea && !this.static;
    }

    onCollapsibleTextareaFocus() {
        if (this.isCollapsibleTextarea) {
            this.setTextareaExpanded(true);
        }
    }

    /** Collapses again once focus has left both the textarea and its buttons, unless pinned open. */
    onCollapsibleFocusLeave(event: FocusEvent) {
        if (!this.isCollapsibleTextarea || this.textareaPinnedOpen) {
            return;
        }

        const next = event.relatedTarget as Node | null;
        const staysInControl =
            !!next &&
            (this.textareaEl?.nativeElement.contains(next) || this.textareaActions?.nativeElement.contains(next));
        if (!staysInControl) {
            this.setTextareaExpanded(false);
        }
    }

    toggleTextarea() {
        if (this.textareaExpanded) {
            this.textareaPinnedOpen = false;
            this.setTextareaExpanded(false);

            // Otherwise it would stay focused, and so expanded again the moment it is edited.
            const textarea = this.textareaEl?.nativeElement;
            if (textarea && document.activeElement === textarea) {
                textarea.blur();
            }
        } else {
            this.textareaPinnedOpen = true;
            this.setTextareaExpanded(true);
        }
    }

    private setTextareaExpanded(expanded: boolean) {
        this.textareaExpanded = expanded;
        this.scheduleTextareaLayout();
    }

    // Deferred so the DOM has picked up the latest value / collapsed state before it is measured.
    private scheduleTextareaLayout() {
        if (!this.collapsible) {
            return;
        }

        clearTimeout(this.textareaLayoutTimeout);
        this.textareaLayoutTimeout = setTimeout(() => {
            if (this.textareaExpanded) {
                this.growTextareaToContent();
            } else {
                this.checkTextareaOverflow();
            }
        });
    }

    // Kendo's [autoSize] only measures on init and on `input`, so a value that arrives later (or
    // a field that was collapsed while it changed) needs an explicit resize when it is expanded.
    private growTextareaToContent() {
        const textarea = this.textareaEl?.nativeElement;
        if (!textarea) {
            return;
        }

        textarea.style.height = 'auto';
        textarea.style.height = `${textarea.scrollHeight}px`;
    }

    // Only meaningful while collapsed: the height is then pinned to one line, so any content
    // beyond it shows up as scrollHeight > clientHeight. Expanded, the last result is kept.
    private checkTextareaOverflow() {
        const textarea = this.textareaEl?.nativeElement;
        if (!textarea || this.textareaExpanded) {
            return;
        }

        this.textareaNeedsCollapse = textarea.scrollHeight > textarea.clientHeight + 1;
    }

    ngOnChanges(changes) {
        if (changes && changes.ngModel && changes.ngModel.firstChange) {
            this.writeValue(changes.ngModel.currentValue);
        }

        this.showFormControlValidation();
    }

    private showValidationErrors() {
        if (this.errors.length > 0) {
            this.tooltip.ngbTooltip = map(this.errors, (x) => x.errorMessage).join('\n');
            if (this.showTooltip) {
                // this.tooltip.open();
            }
        } else {
            this.tooltip.ngbTooltip = null;
            this.tooltip.close();
        }
    }

    private showFormControlValidation() {
        if (this.formControlValidators && this.tooltip) {
            this.tooltip.ngbTooltip = map(this.formControlValidators, (x) =>
                this.translateService.instant(marker(x))
            ).join('\n');
        } else {
            if (this.tooltip) {
                this.tooltip.ngbTooltip = null;
            }
        }
    }

    writeValue(obj: any): void {
        this.value = obj;
    }

    registerOnChange(fn: any): void {
        this.onChange = fn;
    }

    registerOnTouched(fn: any): void {
        this.onTouch = fn;
    }

    setDisabledState?(isDisabled: boolean): void {
        // this.isDisabled = isDisabled;
    }

    public isItemSelected(val) {
        return this.value && this.value.some((x) => x === val);
    }

    getTooltip(value) {
        if (!value) {
            return undefined;
        }

        if (!isArray(value)) {
            value = [value];
        }

        const labels = map(
            filter(this.options, (x: any) => value.includes(x.value)),
            (x: any) => x.label
        );

        return labels && labels.join('\n');
    }

    getDateTimeFormatPlaceHolder = (): DateInputCustomFormatPlaceholder => {
        return {
            day: this.translateService.instant(marker('day')),
            month: this.translateService.instant(marker('month')),
            year: this.translateService.instant(marker('year')),
            hour: this.translateService.instant(marker('hour')),
            minute: this.translateService.instant(marker('minute')),
            second: this.translateService.instant(marker('second'))
        };
    };

    getDateFormatPlaceHolder = (): Pick<DateInputCustomFormatPlaceholder, 'year' | 'month' | 'day'> => {
        return {
            day: this.translateService.instant(marker('day')),
            month: this.translateService.instant(marker('month')),
            year: this.translateService.instant(marker('year'))
        };
    };

    getTimeFormatPlaceholder = (): Pick<DateInputCustomFormatPlaceholder, 'hour' | 'minute'> => {
        return {
            hour: this.translateService.instant(marker('hour')),
            minute: this.translateService.instant(marker('minute'))
        };
    };

    /** For app-control of type string (form-textbox) prevent ' ' as first character */
    handleTextboxKeydown($event: KeyboardEvent) {
        if ($event.code === 'Space' && !this.value) {
            $event.preventDefault();
        }
    }

    roundNumber(val) {
        if (val == null || val === '') return val;
        const numValue: number = +val;
        if (this.roundModifier !== 0) {
            return (Math.round((numValue + Number.EPSILON) * this.roundModifier) / this.roundModifier).toString();
        }
        return val;
    }

    toggleFieldTextType() {
        this.fieldTextType = !this.fieldTextType;
    }

    buttonClick() {
        this.textboxWithButtonClick.emit();
    }
}
